import React, { useState, useEffect } from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  DollarSign,
  Activity,
  Users,
  Server,
  TrendingUp,
  RefreshCw,
  Calendar,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import StatCard from "@/shared/components/dashboard/StatCard";
import TransactionChart from "@/shared/components/dashboard/TransactionChart";
import RecentTransactions from "@/shared/components/dashboard/RecentTransactions";
import ProviderStatus from "@/shared/components/dashboard/ProviderStatus";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import { getDateRange, filterTransactionsByDateRange } from "@/shared/utils/dateFilters";
import { format, eachDayOfInterval, eachHourOfInterval, differenceInDays, differenceInHours } from "date-fns";

export default function AdminDashboard() {
  const { t } = useLanguage();
  const [dateFilter, setDateFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });

  // TODO: Implement custom API calls
  const { data: transactions = [], isLoading: txLoading, refetch: refetchTx } = useQuery({
    queryKey: ["transactions"],
    queryFn: async () => [], // TODO: Implement Transaction.list API call
  });
   
  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: async () => [], // TODO: Implement Merchant.list API call
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => [], // TODO: Implement Provider.list API call
  });

  // Get date range based on filter
  const dateRange = React.useMemo(() => {
    return getDateRange(dateFilter, customDateRange);
  }, [dateFilter, customDateRange]);

  // Filter transactions by date range
  const filteredTransactions = React.useMemo(() => {
    return filterTransactionsByDateRange(
      transactions,
      dateRange.startDate,
      dateRange.endDate
    );
  }, [transactions, dateRange]);

  // Calculate stats
  const stats = React.useMemo(() => {
    const deposits = filteredTransactions.filter(t => t.type === "deposit");
    const withdrawals = filteredTransactions.filter(t => t.type === "withdrawal");
    const successDeposits = deposits.filter(t => t.status === "success");
    const successWithdrawals = withdrawals.filter(t => t.status === "success");

    return {
      totalDeposits: successDeposits.reduce((sum, t) => sum + (t.amount || 0), 0),
      totalWithdrawals: successWithdrawals.reduce((sum, t) => sum + (t.amount || 0), 0),
      depositCount: deposits.length,
      withdrawalCount: withdrawals.length,
      successRate: filteredTransactions.length > 0 
        ? ((successDeposits.length + successWithdrawals.length) / filteredTransactions.length * 100).toFixed(1)
        : 100,
      pendingCount: filteredTransactions.filter(t => t.status === "pending" || t.status === "processing").length,
    };
  }, [filteredTransactions]);

  // Generate chart data based on date range
  const chartData = React.useMemo(() => {
    const { startDate, endDate } = dateRange;
    const daysDiff = differenceInDays(endDate, startDate);
    const hoursDiff = differenceInHours(endDate, startDate);

    // If range is less than 2 days, show hourly data
    if (hoursDiff <= 48) {
      const hours = eachHourOfInterval({ start: startDate, end: endDate });
      return hours.map((hour) => {
        const hourTxs = filteredTransactions.filter(
          (tx) => {
            const txDate = new Date(tx.created_date);
            return txDate >= hour && txDate < new Date(hour.getTime() + 60 * 60 * 1000);
          }
        );
        const deposits = hourTxs
          .filter((tx) => tx.type === "deposit" && tx.status === "success")
          .reduce((sum, tx) => sum + (tx.amount || 0), 0);
        const withdrawals = hourTxs
          .filter((tx) => tx.type === "withdrawal" && tx.status === "success")
          .reduce((sum, tx) => sum + (tx.amount || 0), 0);
        return {
          name: format(hour, "HH:mm"),
          deposits,
          withdrawals,
        };
      });
    } else {
      // Show daily data
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      return days.map((day) => {
        const dayTxs = filteredTransactions.filter(
          (tx) => {
            const txDate = new Date(tx.created_date);
            return txDate >= day && txDate < new Date(day.getTime() + 24 * 60 * 60 * 1000);
          }
        );
        const deposits = dayTxs
          .filter((tx) => tx.type === "deposit" && tx.status === "success")
          .reduce((sum, tx) => sum + (tx.amount || 0), 0);
        const withdrawals = dayTxs
          .filter((tx) => tx.type === "withdrawal" && tx.status === "success")
          .reduce((sum, tx) => sum + (tx.amount || 0), 0);
        return {
          name: format(day, "MMM d"),
          deposits,
          withdrawals,
        };
      });
    }
  }, [filteredTransactions, dateRange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("dashboard")}</h1>
          <p className="text-slate-500">{t("paymentOrchestration")}</p>
        </div>
        <div className="flex items-center gap-3">
          <DateFilter
            value={dateFilter}
            onChange={setDateFilter}
            customDateRange={customDateRange}
            onCustomDateRangeChange={setCustomDateRange}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetchTx()}
            className="bg-white"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t("totalDeposits")}
          value={`฿${stats.totalDeposits.toLocaleString()}`}
          change="+12.5%"
          changeType="positive"
          icon={ArrowDownToLine}
          iconColor="bg-blue-500"
          delay={0}
        />
        <StatCard
          title={t("totalWithdrawals")}
          value={`฿${stats.totalWithdrawals.toLocaleString()}`}
          change="+8.2%"
          changeType="positive"
          icon={ArrowUpFromLine}
          iconColor="bg-emerald-500"
          delay={0.1}
        />
        <StatCard
          title={t("successRate")}
          value={`${stats.successRate}%`}
          change="+2.1%"
          changeType="positive"
          icon={Activity}
          iconColor="bg-purple-500"
          delay={0.2}
        />
        <StatCard
          title={t("activeMerchants")}
          value={merchants.filter(m => m.status === "active").length.toString()}
          change="+3"
          changeType="positive"
          icon={Users}
          iconColor="bg-amber-500"
          delay={0.3}
        />
      </div>

      {/* Charts & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TransactionChart data={chartData} />
        </div>
        <ProviderStatus providers={providers} />
      </div>

      {/* Recent Transactions & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RecentTransactions 
            transactions={filteredTransactions} 
            onViewAll={() => window.location.href = createPageUrl("Transactions")}
          />
        </div>
        
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4">{t("quickActions")}</h3>
          <div className="space-y-3">
            <Link to={createPageUrl("Merchants")}>
              <Button variant="outline" className="w-full justify-start gap-3 h-12">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{t("addMerchant")}</p>
                  <p className="text-xs text-slate-500">{t("onboardNewMerchant")}</p>
                </div>
              </Button>
            </Link>
            <Link to={createPageUrl("Providers")}>
              <Button variant="outline" className="w-full justify-start gap-3 h-12">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Server className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{t("configureProvider")}</p>
                  <p className="text-xs text-slate-500">{t("addPaymentProvider")}</p>
                </div>
              </Button>
            </Link>
            <Link to={createPageUrl("Routing")}>
              <Button variant="outline" className="w-full justify-start gap-3 h-12">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{t("routingRules")}</p>
                  <p className="text-xs text-slate-500">{t("configureRouting")}</p>
                </div>
              </Button>
            </Link>
          </div>

          {/* Pending Alerts */}
          <div className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-2 text-amber-700">
              <Activity className="h-4 w-4" />
              <span className="font-medium">{stats.pendingCount} {t("pendingTransactions")}</span>
            </div>
            <p className="text-sm text-amber-600 mt-1">
              {t("requiresAttention")}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}